// ── Curated "meaningful change" classifier ──
//
// Single source of truth shared by the diff card (buildActivityGroups in
// mcp-chat-diff.ts) and the rating gate (buildChangesSummary in
// mcp-chat-utils.ts). Given a completed tool activity, it returns the
// user-facing category label for a *meaningful artifact change* — or null
// for everything the user does not care about (reads, internal sweeps,
// specialist runs, component-creation that the Components list already shows).
//
// Design rule: this is an explicit ALLOW-LIST. Anything not matched returns
// null. There is deliberately NO "Other" catch-all — that catch-all is what
// leaked "Backend Architect run" / "Listing actions" into the card before.

// Category for edits to existing components. Build-time configures on just-added
// components are filtered from this group (see buildActivityGroups) so it never
// duplicates the "Added components" list.
export const UPDATED_COMPONENTS_CATEGORY = "Updated components";

export interface ChangeCategoryInput {
  toolName: string;
  operation?: string;
  /** Canonical read/write flag, computed once at activity-creation time. */
  isMutating: boolean;
  /** True when the activity is a synthesized specialist run (Designer/Wirer/Binder/Backend Architect). */
  isSpecialist: boolean;
}

// Tool names whose whole job is creating components. The created components
// are already enumerated in the "Components" list, so they must never also
// surface as a redundant activity row.
const COMPONENT_CREATION_TOOLS: ReadonlySet<string> = new Set([
  "ui_render",
  // Legacy granular builders (retired, kept for restored old transcripts).
  "ui_add_components",
  "ui_add_form_field",
  "ui_add_form_row",
  "ui_add_form_section",
  "ui_build_form",
  "ui_add_section",
  "ui_add_card_grid",
  "ui_build_page"
]);

// ui_project operations that represent a real, user-visible artifact change.
// Everything else on ui_project (save / reload_* / preview / navigate_* and
// the post-build set_screen_role homepage sweep) is internal plumbing.
const UI_PROJECT_AUTH_OPS: ReadonlySet<string> = new Set([
  "create_role",
  "update_role",
  "delete_role",
  "set_screen_authorization"
]);

function categorizeKnownTool(toolName: string, operation?: string): string | null {
  switch (toolName) {
    case "ui_component":
      // "add" is component creation (shown in "Added components"); "select" is a read.
      if (operation === "add" || operation === "select") return null;
      // Edits to existing components (configure/move/rename/delete/…). Build-time
      // configures on just-added components target synthetic ids and are filtered
      // out in buildActivityGroups so this group never duplicates "Added components".
      return UPDATED_COMPONENTS_CATEGORY;
    case "ui_style":
      return "Styles";
    case "ui_action":
    case "ui_action_extended":
      return "Actions";
    case "ui_property":
      return "Data Binding";
    case "ui_screen":
      // Navigation (incl. the post-build navigate sweep) is not an artifact change.
      if (operation === "navigate") return null;
      return "Screens";
    case "ui_screen_input":
      return "Screen Inputs";
    case "ui_validation":
      return "Validations";
    case "ui_project":
      if (operation && UI_PROJECT_AUTH_OPS.has(operation)) return "Authorization";
      return null;
    case "ui_project_extended":
      if (!operation) return "Project";
      // Navigation (navigate_to_workflow / _scheduled_job / _api) is not an
      // artifact change — guard before the substring buckets below.
      if (operation.startsWith("navigate")) return null;
      if (operation.includes("role") || operation.includes("authorization")) return "Authorization";
      if (operation.includes("workflow")) return "Workflows";
      if (operation.includes("scheduled")) return "Scheduled Jobs";
      if (operation.includes("folder")) return "Folders";
      if (operation.includes("image")) return "Images";
      if (operation.includes("comment")) return null;
      return "Project";
    case "ui_microapp":
      return "Micro Apps";
    case "ui_history":
      // undo / redo are meta-operations, not artifact changes.
      return null;
    default:
      break;
  }

  // Backend tools by prefix.
  if (toolName.startsWith("database_schema") || toolName.startsWith("database_data")) return "Database";
  if (toolName.startsWith("db_sql_action") || toolName.startsWith("datasource_")) return "Datasources";
  if (toolName.startsWith("ds_sap") || toolName.startsWith("ds_datasource") || toolName.startsWith("ds_action")) {
    return "Datasources";
  }
  if (toolName.startsWith("ds_csharp") || toolName.startsWith("csharp_")) return "Custom Code";
  if (toolName.startsWith("lsp_csharp")) return null; // diagnostics / format only
  if (toolName.startsWith("translation")) return "Translations";
  if (toolName.startsWith("api_management")) return "APIs";
  if (toolName.startsWith("scheduler_job")) return "Scheduled Jobs";
  if (toolName.startsWith("offline_")) return "Offline Tables";
  if (toolName.startsWith("package_management")) return "Packages";
  if (toolName.startsWith("crud_")) return "Screens";
  if (toolName.startsWith("screen_")) return "Screens";

  return categorizeLegacyTool(toolName);
}

// Old-style granular tool names (retired). Reached only for mutating, non-
// category, non-prefixed tools — i.e. restored legacy transcripts. No "Other"
// fallback: unknown legacy names resolve to null.
function categorizeLegacyTool(toolName: string): string | null {
  if (toolName.includes("style") || toolName.includes("theme") || toolName.includes("design_fixes")) return "Styles";
  if (toolName.includes("rename_component") || toolName.includes("configure")) return "Components";
  if (
    toolName.includes("delete_component") ||
    toolName.includes("move_component") ||
    toolName.includes("clone_component") ||
    toolName.includes("reorder_component")
  ) {
    return "Components";
  }
  if (toolName.includes("validation")) return "Validations";
  if (toolName.includes("action")) return "Actions";
  if (toolName.includes("sql")) return "Datasources";
  if (toolName.includes("screen")) return "Screens";
  return null;
}

/**
 * Returns the display category for a meaningful artifact change, or null to
 * hide the activity entirely (reads, specialist runs, component creation,
 * internal sweeps, undo/redo).
 */
export function categorizeArtifactChange(input: ChangeCategoryInput): string | null {
  const { toolName, operation, isMutating, isSpecialist } = input;

  // 1. Specialist runs (Designer / Wirer / Binder / Backend Architect) are internal.
  if (isSpecialist || toolName.startsWith("specialist:")) return null;

  // 2. Trust the canonical read/write flag.
  if (!isMutating) return null;

  // 3. Component creation is already represented by the Components list.
  if (toolName.startsWith("batch(")) return null;
  if (COMPONENT_CREATION_TOOLS.has(toolName)) return null;

  // 4. Curated allow-list (default deny).
  return categorizeKnownTool(toolName, operation);
}
